import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown, RotateCcw, Search } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/EmptyState";
import { cn } from "@/lib/utils";
import { formatArabicNumber } from "@/lib/formatters";
import {
  getPermissionGroup,
  getPermissionLabel,
} from "@/features/permissions/constants/permission-labels";

interface PermissionGroupListProps {
  permissions: string[];
  /** Renders the control (switch / segmented control) for a single permission. */
  renderControl: (permission: string) => ReactNode;
  /** Whether a permission is currently "on" — drives the per-group count badge. */
  isActive: (permission: string) => boolean;
  /** Optional secondary line under the permission label (e.g. effective/custom badges). */
  renderMeta?: (permission: string) => ReactNode;
  /** Optional per-group reset (e.g. clear overrides back to inherited). */
  onResetGroup?: (permissions: string[]) => void;
}

export function PermissionGroupList({
  permissions,
  renderControl,
  isActive,
  renderMeta,
  onResetGroup,
}: PermissionGroupListProps) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const normalizedQuery = query.trim();

  const grouped = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const permission of permissions) {
      const group = getPermissionGroup(permission);
      (groups[group] ??= []).push(permission);
    }
    return groups;
  }, [permissions]);

  const filteredGroups = useMemo(() => {
    if (!normalizedQuery) return grouped;
    const result: Record<string, string[]> = {};
    for (const [group, perms] of Object.entries(grouped)) {
      const matches = perms.filter(
        (perm) =>
          getPermissionLabel(perm).includes(normalizedQuery) ||
          perm.includes(normalizedQuery),
      );
      if (matches.length > 0) result[group] = matches;
    }
    return result;
  }, [grouped, normalizedQuery]);

  const groupEntries = Object.entries(filteredGroups);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="ابحث عن صلاحية"
          className="pr-9"
        />
      </div>

      {groupEntries.length === 0 ? (
        <EmptyState
          icon={Search}
          title="لا توجد صلاحيات مطابقة"
          description="جرّب تعديل كلمة البحث."
        />
      ) : (
        groupEntries.map(([group, perms]) => {
          const activeCount = perms.filter(isActive).length;
          const isOpen = normalizedQuery ? true : !collapsed[group];
          return (
            <Collapsible
              key={group}
              open={isOpen}
              onOpenChange={(next) =>
                setCollapsed((prev) => ({ ...prev, [group]: !next }))
              }
              className="rounded-lg border bg-card"
            >
              <div className="flex items-center gap-2 px-4 py-3">
                <CollapsibleTrigger className="flex flex-1 items-center gap-2 text-right outline-none">
                  <ChevronDown
                    className={cn(
                      "size-4 shrink-0 text-muted-foreground transition-transform",
                      !isOpen && "-rotate-90",
                    )}
                  />
                  <span className="font-semibold text-foreground">{group}</span>
                  <Badge
                    variant="secondary"
                    className="rounded-full tabular-nums"
                  >
                    {formatArabicNumber(activeCount)}/
                    {formatArabicNumber(perms.length)}
                  </Badge>
                </CollapsibleTrigger>
                {onResetGroup && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => onResetGroup(perms)}
                  >
                    <RotateCcw className="size-3.5" />
                    <span>إعادة</span>
                  </Button>
                )}
              </div>
              <CollapsibleContent>
                <div className="border-t">
                  {perms.map((perm) => (
                    <div
                      key={perm}
                      className="flex items-center justify-between gap-4 border-b px-4 py-3 last:border-0"
                    >
                      <div className="shrink-0">{renderControl(perm)}</div>
                      <div className="flex-1 text-right">
                        <p className="text-sm text-foreground">
                          {getPermissionLabel(perm)}
                        </p>
                        {renderMeta && (
                          <div className="mt-1 flex justify-end gap-1">
                            {renderMeta(perm)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })
      )}
    </div>
  );
}
